import { Injectable } from '@angular/core';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import jwt_decode from 'jwt-decode';
import { UnauthorizedError } from 'src/app/models/errors/unauthorizederror';
import { IUser } from 'src/app/models/user.interface';
import { environment } from 'src/environments/environment';
import { UtilService } from '../util.service';
import { BehaviorSubject, Observable, combineLatest, map, shareReplay } from 'rxjs';
import { from } from 'rxjs';
import { UserService } from '../user.service';
import { AccountService } from '../account.service';
import { StorageService } from '../storage.service';


@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private apiUrl: string = environment.apiUrl;
    private user: IUser | null = null;
    private token: string | null = null;

    public initialized = new BehaviorSubject<boolean>(false);
    public authState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Tracks whether the client is in a pending-invite state (user has an invite token but hasn't accepted yet)
    private pendingInviteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public pendingInvite$ = this.pendingInviteSubject.asObservable();

    private userSubject = new BehaviorSubject<IUser | null>(null);
  public user$ = this.userSubject.asObservable();
  private userFetchInProgress: Promise<IUser | null> | null = null;
    // Indicates the user has no accounts/default account and should be shown the "register-existing-user" sales page
    // Derived observable computed from canonical `user` + accounts state so it can't drift out of sync.
    public needsPlan$: Observable<boolean>;



    constructor(private util: UtilService,
        private storageService: StorageService,
        private userService: UserService,
        private accountService: AccountService) {
        // Build derived needsPlan$ observable now that accountService is available
        this.needsPlan$ = combineLatest([this.user$, this.accountService.accountsObservable()]).pipe(
            map(([user, accounts]) => {
                const accountsList = Array.isArray(accounts) ? accounts : [];
                const hasAccount = accountsList.length > 0 || !!(user && user.defaultAccountId);
                return !hasAccount;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.initializeService();
    }

    // Global refresh promise to avoid concurrent refreshes across instances
    private getRefreshPromiseKey(): string {
        return '__socialDash_refreshPromise';
    }

    private setGlobalRefreshPromise(p: Promise<boolean> | null) {
        try {
            (window as any)[this.getRefreshPromiseKey()] = p;
        } catch (e) { /* ignore */ }
    }

    private getGlobalRefreshPromise(): Promise<boolean> | null {
        try {
            return (window as any)[this.getRefreshPromiseKey()] || null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Reads a header value case-insensitively from Capacitor response headers.
     */
    private getHeaderValue(headers: unknown, headerName: string): string | null {
        if (!headers || typeof headers !== 'object') {
            return null;
        }

        const target = headerName.toLowerCase();
        const entries = Object.entries(headers as Record<string, unknown>);
        const found = entries.find(([key]) => key.toLowerCase() === target);
        if (!found) {
            return null;
        }

        const value = found[1];
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        return null;
    }

    /** Store refresh token (if server returns one). */
    async storeRefreshToken(token: string | null) {
        try {
            if (token) {
                await this.storageService.set('refreshToken', token);
            } else {
                await this.storageService.remove('refreshToken');
            }
        } catch (e) {
            console.warn('Failed to store refresh token', e);
        }
    }

    async getRefreshToken(): Promise<string | null> {
        try {
            await this.storageService.waitUntilReady();
            const t = await this.storageService.get<string>('refreshToken');
            return t || null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Attempt to refresh the access token using the stored refresh token.
     * Returns true if refresh succeeded and token stored; false otherwise.
     * Only calls logout() when the server explicitly rejects the refresh token (401/403).
     * Network errors and server errors (5xx) do not trigger logout — they are transient.
     */
    async refreshToken(): Promise<boolean> {
        // If a refresh is already in progress, reuse its promise
        const existing = this.getGlobalRefreshPromise();
        if (existing) return existing;

        const promise = (async (): Promise<boolean> => {
            try {
                const refreshToken = await this.getRefreshToken();
                if (!refreshToken) {
                    // No refresh token stored — session is old or storage was cleared.
                    // Do not logout here; the caller can decide based on context.
                    console.debug('[AuthService] no refresh token available');
                    return false;
                }

                const url = `${this.apiUrl}/auth/refresh`;
                const options = {
                    url,
                    headers: { 'Content-Type': 'application/json' },
                    data: { refreshToken }
                } as any;

                // Use Capacitor HTTP so we avoid HttpClient circular deps
                const resp = await CapacitorHttp.post(options as any);

                if (resp && resp.status === 200) {
                    // Server may send new access token in header or body
                    const newTokenHeader = this.getHeaderValue(resp.headers, 'x-token');
                    const newAccessToken = newTokenHeader || (resp.data && resp.data.token ? resp.data.token : null);
                    const newRefresh = resp.data && resp.data.refreshToken ? resp.data.refreshToken : null;

                    if (newAccessToken) {
                        await this.setAuthToken(newAccessToken);
                        if (newRefresh) await this.storeRefreshToken(newRefresh);
                        console.debug('[AuthService] token refreshed successfully');
                        return true;
                    }
                }

                // Server explicitly rejected the refresh token — safe to logout
                if (resp && (resp.status === 401 || resp.status === 403)) {
                    console.warn('[AuthService] refresh token rejected by server, logging out');
                    await this.logout();
                    return false;
                }

                // Any other failure (500, bad response, missing token in body) — don't logout.
                // Could be a transient server error; user will retry on next action.
                console.warn('[AuthService] refresh failed with status', resp?.status, '— will retry on next request');
                return false;
            } catch (e) {
                // Network error or exception — do not logout, this may be transient.
                console.warn('[AuthService] refresh request failed (network/exception) — will retry on next request', e);
                return false;
            } finally {
                // clear global promise
                this.setGlobalRefreshPromise(null);
            }
        })();

        this.setGlobalRefreshPromise(promise);
        return promise;
    }

    /**
     * Ensure the current access token is valid (and proactively refresh if expiring soon).
     * The default threshold of 300s (5 min) gives a wide refresh window for 15-minute tokens.
     * Returns a valid token or null if none available.
     */
    async ensureValidAccessToken(thresholdSeconds: number = 300): Promise<string | null> {
        const token = await this.getAuthToken();
        if (!token) return null;

        const exp = this.getTokenExpiration(token);
        const now = Math.floor(Date.now() / 1000);
        if (exp && exp - now <= thresholdSeconds) {
            const ok = await this.refreshToken();
            if (!ok) return null;
            const t = await this.getAuthToken();
            return t || null;
        }
        return token;
    }


    async initializeService(): Promise<void> {
        if (this.token) {
            this.initialized.next(true);
            return;
        }
        try {
            await this.storageService.waitUntilReady();
            this.token = await this.getAuthToken();
            // initialize pending-invite flag from storage if present
            try {
                const pending = await this.storageService.get<string>('pendingInviteToken');
                this.pendingInviteSubject.next(!!pending);
            } catch (e) {
                this.pendingInviteSubject.next(false);
            }
            if (this.token) {
                this.authStateUpdater(true);
            }
        } catch (error) {
            console.error('Error during AuthService initialization:', error);
        } finally {
            this.initialized.next(true); // Mark initialization as complete
        }
    }

    /**
     * Persist or clear a pending invite token. When set, the client will consider the session
     * as being in an "invite acceptance" flow and UI should hide normal navigation until acceptance.
     */
    async setPendingInviteToken(token: string | null): Promise<void> {
        try {
            if (token) {
                await this.storageService.set('pendingInviteToken', token);
                this.pendingInviteSubject.next(true);
            } else {
                await this.storageService.remove('pendingInviteToken');
                this.pendingInviteSubject.next(false);
            }
        } catch (err) {
            console.warn('Failed to set pending invite token', err);
            this.pendingInviteSubject.next(!!token);
        }
    }


    /** Login */
    async login(username: string, password: string): Promise<HttpResponse> {
        const body = {
            username,
            password
        };

        const url = `${this.apiUrl}/auth/login`;

        const options = {
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: body,
        };

        const response: HttpResponse = await CapacitorHttp.post(options);

        switch (response.status) {
            case 401:
                throw new UnauthorizedError('Invalid credentials.');
            case 429:
                // surface server-provided message for lockout/rate-limit
                const message = (response.data && (response.data.message || response.data.error)) || 'Too many attempts. Please try again later.';
                const err: any = new Error(message);
                err.status = 429;
                err.error = { message };
                throw err;
        }

        const accessToken = this.getHeaderValue(response.headers, 'x-token');
        if (accessToken) {
            const token = accessToken;
            const loggedInUser = response.data;

            // linked account token storage removed; keep primary token
            await this.setAuthToken(token);
            this.user = loggedInUser;
            // Persist refresh token if returned in response body
            try {
                const body: any = response.data || {};
                if (body.refreshToken) await this.storeRefreshToken(body.refreshToken);
            } catch (e) { console.warn('Failed to store refresh token on login', e); }
        }

        this.authStateUpdater(true);

        return response;
    }

    /** Register */
    async register(newUser: any): Promise<HttpResponse> {
        const url = `${this.apiUrl}/auth/register`;

        const options = {
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: newUser,
        };

        const response: HttpResponse = await CapacitorHttp.post(options);

        // If server returned a token in headers, store it and fetch the user so client is authenticated immediately
        try {
                const accessToken = this.getHeaderValue(response.headers, 'x-token');
                if (accessToken) {
                    const token = accessToken;
                    await this.setAuthToken(token);

                    // Persist refresh token if server returned one at registration time.
                    try {
                        const registerBody: any = response.data || {};
                        if (registerBody.refreshToken) {
                            await this.storeRefreshToken(registerBody.refreshToken);
                        }
                    } catch (e) {
                        console.warn('Failed to store refresh token on register', e);
                    }

                    // If the registration response included the user and accounts payload,
                    // hydrate the client-side caches directly to avoid an extra /users/me request.
                    try {
                        const respBody: any = response.data || {};
                        if (respBody.user) {
                            const user = respBody.user as IUser;
                            this.user = user;
                            this.userSubject.next(user);

                            // Hydrate accounts cache if provided
                            if (Array.isArray(respBody.accounts)) {
                                try { this.accountService.hydrateAccounts(respBody.accounts); } catch (err) { console.warn('Failed to hydrate accounts from register response', err); }
                            }

                            // Select active account if provided — but only set locally during hydration/registration.
                            // The active account is already persisted in the DB; avoid re-posting on page load.
                            try {
                                if (respBody.activeAccount) {
                                    try { this.accountService.setActiveAccountLocal(respBody.activeAccount); } catch (e) { console.warn('Failed to set local active account from register response', e); }
                                } else if (respBody.activeAccountId && Array.isArray(respBody.accounts)) {
                                    const found = respBody.accounts.find((a: any) => a._id === respBody.activeAccountId);
                                    if (found) try { this.accountService.setActiveAccountLocal(found); } catch (e) { console.warn('Failed to set local active account from register response', e); }
                                }
                            } catch (err) { console.warn('Failed to select active account from register response', err); }
                        } else {
                            // Fallback: fetch canonical user state
                            await this.getUser();
                        }
                    } catch (err) {
                        // If anything fails, fall back to calling getUser()
                        console.warn('Error processing register response body, fetching user instead', err);
                        try { await this.getUser(); } catch (_) {}
                    }
                }
        } catch (err) {
            console.warn('Failed to store token after register:', err);
        }

        return response;
    }


    /**
     * Reset user password
     * @param email email
     * @returns 
     */
    async resetPasswordRequest(email: string): Promise<HttpResponse> {
        const url = `${this.apiUrl}/auth/reset-password-request`;

        const body = { email };

        const options = {
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: body,
        };

        const response: HttpResponse = await CapacitorHttp.post(options);

        return response;
    }


    /**
* Reset user password
* @param resetToken resetToken
* @returns 
*/
    async resetPassword(resetToken: string, password: string): Promise<HttpResponse> {
        const url = `${this.apiUrl}/auth/reset-password`;

        const body = {
            'resetToken': resetToken,
            'newPassword': password
        };

        const options = {
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: body,
        };

        const response: HttpResponse = await CapacitorHttp.post(options);

        return response;
    }


    /**
     * Register a user from an invitation.
     * @param token The invitation token.
     * @param userData The user's first name, last name, and password.
     * @returns
     */
    async registerFromInvite(token: string, userData: any): Promise<HttpResponse> {
        const url = `${this.apiUrl}/auth/register/invite`;
        const body = {
            token,
            ...userData
        };

        const options = {
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: body,
        };

        const response: HttpResponse = await CapacitorHttp.post(options);

        if (response.status === 201 && response.data.token) {
            await this.setAuthToken(response.data.token);
            try {
                const inviteBody: any = response.data || {};
                if (inviteBody.refreshToken) {
                    await this.storeRefreshToken(inviteBody.refreshToken);
                }
            } catch (e) {
                console.warn('Failed to store refresh token on invite registration', e);
            }
            // The user is now logged in.
        }

        return response;
    }


   /**
   * Get the current user (cached or fetch if not available)
   */
   async getUser(): Promise<IUser | null> {
    // If user is already cached, return it
    if (this.userSubject.value) {
      return this.userSubject.value;
    }

    // Avoid multiple fetches by reusing the same promise
    if (this.userFetchInProgress) {
      return this.userFetchInProgress;
    }

        // Start fetching user
        this.userFetchInProgress = (async () => {
            try {
                const token = await this.storageService.get<string>('token');
                if (!token) return null;

                // Use the wrapper endpoint /users/me to get user + accounts in one call
                const resp = await this.userService.getMe().toPromise();
                if (!resp) return null;

                // If response contains accounts, hydrate AccountService cache only if
                // the client doesn't already have accounts cached. This avoids
                // redundant GET /api/accounts calls and prevents server /users/me
                // hydration from clobbering a client-side selection made while the
                // user was interacting with the UI.
                if (resp.accounts && Array.isArray(resp.accounts)) {
                    try {
                        // Clone server-provided accounts to avoid sharing references
                        // that could be mutated by other parts of the client.
                        const serverAccounts = JSON.parse(JSON.stringify(resp.accounts || []));
                        const cached = this.accountService.getAccountsValue();
                        if (!cached || (Array.isArray(cached) && cached.length === 0)) {
                            console.debug('[AuthService] hydrating accounts from /users/me; count=', serverAccounts.length);
                            this.accountService.hydrateAccounts(serverAccounts);
                        } else {
                            // Client already has accounts cached; reconcile by ensuring
                            // any persisted activeAccountId is resolved against the cache
                            const activeId = (() => { try { return localStorage.getItem('activeAccountId'); } catch (e) { return null; }})();
                            if (activeId) {
                                const found = (cached || []).find(a => a._id === activeId);
                                // During hydration prefer to adopt the server/client cached account locally
                                // rather than re-persisting it. Use AccountService.setActiveAccountLocal
                                // to avoid sending a network request on startup.
                                if (found) try { this.accountService.setActiveAccountLocal(found); } catch (e) { console.warn('Failed to set local active account from cache', e); }
                            }
                        }
                    } catch (err) { console.warn('Failed to hydrate accounts', err); }
                }

                // If server returned an activeAccount, select it so UI components reflect it.
                // However, avoid unconditionally overwriting a client-selected active account.
                try {
                    const clientActive = this.accountService.getActiveAccount();

                    if (resp.activeAccount) {
                        // Clone server object before selecting to avoid sharing references
                        const serverActive = JSON.parse(JSON.stringify(resp.activeAccount || {}));
                        // Only select the server activeAccount if the client has no active account
                        // or the client active account is just a placeholder (no name/logo).
                        const serverActiveId = (serverActive && (serverActive._id || serverActive.id)) || null;
                        const clientActiveId = clientActive && (clientActive._id || (clientActive as any).id) || null;

                        const clientHasDetails = clientActive && (clientActive.name || clientActive.logo);

                        if (!clientActive || !clientHasDetails) {
                            // client has no real active account — adopt server's locally (do not re-persist)
                            try { this.accountService.setActiveAccountLocal(serverActive); } catch (e) { console.warn('Failed to set local active account', e); }
                        } else if (serverActiveId && clientActiveId && serverActiveId === clientActiveId) {
                            // same account id — refresh local cache with server-provided object
                            try { this.accountService.setActiveAccountLocal(serverActive); } catch (e) { console.warn('Failed to refresh local active account', e); }
                        } else {
                            // client has a selected account that appears to be intentional; keep it and do not override
                            // (this prevents a server 304 or stale activeAccount from clobbering client state)
                        }
                    }
                    else if (resp.user && resp.user.defaultAccountId) {
                        // Only select defaultAccountId if the client has no active account.
                        const clientActive = this.accountService.getActiveAccount();
                        if (!clientActive) {
                            const accounts = Array.isArray(resp.accounts) ? resp.accounts : [];
                            const found = accounts.find((a: any) => a._id === resp.user.defaultAccountId);
                            if (found) try { this.accountService.setActiveAccountLocal(found); } catch (e) { console.warn('Failed to set local default account', e); }
                        }
                    }
                } catch (err) {
                    console.warn('Failed to select active account', err);
                }

                const user = resp.user ? resp.user as IUser : resp as IUser;
                this.userSubject.next(user); // Update BehaviorSubject
                // If the server returned accounts list, or the user has a defaultAccountId, they have an account.
                const accountsList = resp.accounts && Array.isArray(resp.accounts) ? resp.accounts : [];
                const hasAccount = (accountsList.length > 0) || (!!(user && user.defaultAccountId));
                // DEBUG: Log full response and computed account state for troubleshooting register-existing-user redirect
                try {
                    console.debug('[AuthService] /users/me resp=', resp);
                    console.debug('[AuthService] /users/me accounts.length=', accountsList.length, 'user.defaultAccountId=', user?.defaultAccountId, 'hasAccount=', hasAccount);
                } catch (logErr) {
                    // ignore logging errors
                }
                this.user = user;
                return user;
            } catch (error) {
                console.error('Error fetching user:', error);
                // Do NOT call logout() here for all errors.
                // refreshToken() in auth.service.ts already calls logout() when the
                // server explicitly rejects the refresh token (401/403 from /auth/refresh).
                // Calling logout() here on transient network or server errors would destroy
                // a valid refresh token, permanently logging the user out unnecessarily.
                return null;
            } finally {
                this.userFetchInProgress = null; // Reset fetch state
            }
        })();

    return this.userFetchInProgress;
  }

  /**
   * Observable Fetch for Components
   */
  fetchUser(): Observable<IUser | null> {
    this.getUser(); // Ensure data is fetched
    return this.user$;
  }


    /**
     * Store the token in localStorage
     */
    async storeAuthToken(token: string) {
        this.token = token;
        await this.storageService.set('token', token);
    }


    /**
     * Set the auth token and update user state
     * @param token 
     */
    async setAuthToken(token: string): Promise<void> {
        this.token = token;
        await this.storageService.set('token', token);
        this.authStateUpdater(true);
    }


    /** Get authentication token */
    async getAuthToken(): Promise<string> {
        if (this.token) return this.token;

        await this.storageService.waitUntilReady();
        this.token = await this.storageService.get<string>('token');
        return this.token || '';
    }


    // linked account token helpers removed; use Accounts feature instead


    /** Clear the token from localStorage on logout */
    async logout(): Promise<void> {
        try {
            // Best-effort: notify the server to revoke the refresh-token family.
            // The local clear in `finally` runs regardless of the outcome so that
            // logout always succeeds locally, including offline.
            const refreshToken = await this.getRefreshToken();
            if (refreshToken) {
                const url = `${this.apiUrl}/auth/logout`;
                try {
                    await CapacitorHttp.post({
                        url,
                        headers: { 'Content-Type': 'application/json' },
                        data: { refreshToken },
                    } as any);
                } catch (e) {
                    console.warn('[AuthService] server logout call failed (network/timeout); clearing local state anyway', e);
                }
            }
        } finally {
            await this.storageService.clear();
            this.user = null;
            this.token = null;
            this.authStateUpdater(false);
        }
    }

    /**
     * Synchronous convenience getter for callers that need the current needsPlan value
     */
    public get needsPlanValue(): boolean {
        const user = this.userSubject.value;
        const accounts = (() => { try { return this.accountService.getAccountsValue(); } catch (e) { return null; } })();
        const accountsList = Array.isArray(accounts) ? accounts : [];
        const hasAccount = accountsList.length > 0 || !!(user && user.defaultAccountId);
        return !hasAccount;
    }


    /** Implement the logic to check if the user is authenticated (e.g., check if the token exists and not expired) */
    async isAuthenticated(): Promise<boolean> {
        const token = await this.getAuthToken();

        if (!token) {
            return false;
        }

        // Check if the token has expired
        const tokenExpiration = this.getTokenExpiration(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (tokenExpiration != null && currentTime < tokenExpiration) {
            return true;
        }

        // Token expired — attempt a silent refresh before declaring unauthenticated.
        // This prevents guards from redirecting to login when a valid refresh token exists.
        const refreshed = await this.refreshToken();
        return refreshed;
    }


    /** Is the user an admin */
    async isAdmin(): Promise<boolean> {
        if (!this.token) {
            return false;
        }
        const user = await this.getUser();
        return user ? user.roles.includes('admin') : false;
    }


    /** Helper function to extract the token expiration time from the token payload */
    private getTokenExpiration(token: string): number | null {
        try {
            const decodedToken: any = jwt_decode(token);
            return decodedToken && decodedToken.exp ? decodedToken.exp : null;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }


    // Function to parse user data from the token
    private async parseUserDataFromToken(token: string): Promise<IUser | null> {
        try {
            if (!token) {
                throw new Error('Token is null or undefined');
            }

            if (this.user) {
                return this.user; // <-- Early return if user is already loaded
            }

            const decodedToken: any = jwt_decode(token); // <-- Token is now guaranteed to be valid
            const userId = decodedToken?.id; // <-- Avoid error if id is missing
            if (!userId) {
                throw new Error('Token does not contain user ID');
            }

            const user = await this.userService.getUser(userId).toPromise(); // <-- Call to backend
            if (user) {
                this.user = user;
                return user;
            }

            return null; // <-- No user returned
        } catch (e: any) {
            const error = e as Error;
            console.error('Error decoding token:', error);
            throw Error(`Error decoding token: ${error.message}`);
        }
    }



    // linked account switching removed — use Accounts feature for multi-account workflows


    authStateUpdater(state: boolean): void {
        if (this.authState.getValue() !== state) {
            this.authState.next(state);
        }
    }

}
